import React from 'react';
import { useData, useField } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { TextField, OptionField, AutoField, DateTimeField, StaticField, PickerModal, Section, ViewLayout } from '../EpcrFields';

const CREW_ROSTER_URL = '/epcr-data/crew_roster.json';

// eCrew.03 (Crew Member Response Role, maxOccurs "M") collapsed to a single
// three-way choice for the UI. Each choice writes both the "At Scene" and
// "Transport" variant of that role, so one designation holds for the whole
// call rather than asking the user to reason about NEMSIS's scene/transport
// phase split.
const ROLE_CODES = {
  primary: ["2403011", "2403013"], // Primary Patient Caregiver – At Scene / Transport
  driver: ["2403001", "2403003"],  // Driver/Pilot – Response / Transport
  other: ["2403005"],              // Other
};
const ROLE_ORDER = [["primary", "Primary"], ["driver", "Driver"], ["other", "Other"]];

function roleKeyFromCodes(raw) {
  const codes = Array.isArray(raw) ? raw : raw ? [raw] : [];
  for (const key of Object.keys(ROLE_CODES)) {
    if (ROLE_CODES[key].some(c => codes.includes(c))) return key;
  }
  return null;
}

export function EpcrViewIncident() {
  const data = useData();
  const crew = useField("eCrew/eCrew.CrewGroup");
  const crewList = Array.isArray(crew) ? crew : [];

  // Selecting an Incident City auto-fills Incident County from the same
  // GNIS record (City.json carries each city's county FIPS code) — county
  // stays a plain, still-editable text field for cities outside the demo's
  // curated 30-city list.
  const handleCityPick = (cityCode) => {
    const entry = data.options("eScene.17").find(o => o.value === cityCode);
    if (entry?.countyFips) data.setReportPath("eScene/eScene.21", entry.countyFips);
  };

  const sections = [
    { id: "response",    label: "Response" },
    { id: "scene",       label: "Scene" },
    { id: "personnel",   label: "Personnel" },
    { id: "disposition", label: "Disposition" },
    { id: "destination", label: "Destination" },
    { id: "times",       label: "Times" },
    { id: "mileage",     label: "Mileage" },
    { id: "additional",  label: "Additional Factors" },
  ];

  return (
    <ViewLayout sections={sections}>

      <Section id="response" num="01" title="Response" meta="eResponse.* · eDispatch.*">
        <div className="fgrid">
          <div className="fcol-12">
            <TextField label="Incident Number" elem="eResponse.03" />
          </div>
          <div className="fcol-12">
            <TextField label="EMS Response Number" elem="eResponse.04" />
          </div>
          <div className="fcol-12">
            <OptionField label="Type of Service Requested" elem="eResponse.05"
              featured={["2205001", "2205009"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Response Mode to Scene" elem="eResponse.23"
              featured={["2223001", "2223003", "2223005"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Additional Response Mode Descriptors" elem="eResponse.24" />
          </div>
          <div className="fcol-12">
            <TextField label="Vehicle Dispatch Location" elem="eResponse.16" />
          </div>
          <div className="fcol-12">
            <TextField label="EMS Vehicle (Unit) Number" elem="eResponse.13" />
          </div>
          <div className="fcol-12">
            <OptionField label="Unit Transport and Equipment Capability" elem="eResponse.07" />
          </div>
          <div className="fcol-12">
            <TextField label="EMS Unit Call Sign" elem="eResponse.14" />
          </div>
          <div className="fcol-12">
            <OptionField label="Dispatch Reason" elem="eDispatch.01"
              featured={["2301013", "2301061", "2301069"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Dispatch Priority (Patient Acuity)" elem="eDispatch.05" />
          </div>
        </div>
      </Section>

      <Section id="scene" num="02" title="Scene" meta="eScene.*">
        <div className="fgrid">
          <div className="fcol-12">
            <OptionField label="Incident Location Type" elem="eScene.09" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident Facility or Location Name" elem="eScene.13" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident Street Address" elem="eScene.15" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident ZIP Code" elem="eScene.19" />
          </div>
          <div className="fcol-12">
            <OptionField label="Incident City" elem="eScene.17" onPick={handleCityPick} />
          </div>
          <div className="fcol-12">
            <TextField label="Incident County" elem="eScene.21" />
          </div>
          <div className="fcol-12">
            <OptionField label="Incident State" elem="eScene.18" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident Country" elem="eScene.22" />
          </div>
        </div>
      </Section>

      <Section id="personnel" num="03" title="Personnel"
        meta={`eCrew.* · ${crewList.length} member${crewList.length === 1 ? "" : "s"}`}>
        <CrewList data={data} crewList={crewList} />
      </Section>

      <Section id="disposition" num="04" title="Disposition" meta="eDisposition.*">
        <div className="fgrid">
          <div className="fcol-12">
            <OptionField label="Unit Disposition" elem="eDisposition.27" />
          </div>
          <div className="fcol-12">
            <OptionField label="Patient Evaluation/Care" elem="eDisposition.28" />
          </div>
          <div className="fcol-12">
            <OptionField label="Crew Disposition" elem="eDisposition.29" />
          </div>
          <div className="fcol-12">
            <OptionField label="Transport Disposition" elem="eDisposition.30" />
          </div>
        </div>
      </Section>

      <Section id="destination" num="05" title="Destination" meta="eDisposition.Destination*">
        <div className="fgrid">
          <div className="fcol-12">
            <TextField label="Destination Name" elem="eDisposition.01" />
          </div>
          <div className="fcol-12">
            <TextField label="Destination Address" elem="eDisposition.03" />
          </div>
          <div className="fcol-12">
            <TextField label="Destination ZIP" elem="eDisposition.07" />
          </div>
          <div className="fcol-12">
            <OptionField label="Type of Destination" elem="eDisposition.21" />
          </div>
          <div className="fcol-12">
            <AutoField label="Reason for Destination Choice" elem="eDisposition.20" />
          </div>
        </div>
      </Section>

      <Section id="times" num="06" title="Times" meta="eResponse.08–12">
        <div className="fgrid">
          <div className="fcol-12">
            <div className="time-rows">
              <div className="time-row"><DateTimeField label="PSAP Call" elem="eTimes.01" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Dispatch Notified" elem="eTimes.02" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Unit Notified by Dispatch" elem="eTimes.03" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Unit En Route" elem="eTimes.05" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Unit Arrived on Scene" elem="eTimes.06" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Arrived at Patient" elem="eTimes.07" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Unit Left Scene" elem="eTimes.09" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Patient Arrived at Destination" elem="eTimes.11" nem={null} /></div>
              <div className="time-row"><DateTimeField label="Unit Back in Service" elem="eTimes.13" nem={null} /></div>
            </div>
          </div>

          {/* Delay fields */}
          <div className="fcol-12">
            <AutoField label="Dispatch Delays" elem="eResponse.08"
              shortcuts={[{ label: "None/No Delay", value: "2208013" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Response Delays" elem="eResponse.09"
              shortcuts={[{ label: "None/No Delay", value: "2209011" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Scene Delays" elem="eResponse.10"
              shortcuts={[{ label: "None/No Delay", value: "2210017" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Transport Delays" elem="eResponse.11"
              shortcuts={[{ label: "None/No Delay", value: "2211011" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Turn-Around Delays" elem="eResponse.12"
              shortcuts={[{ label: "None/No Delay", value: "2212015" }]} />
          </div>
        </div>
      </Section>

      <Section id="mileage" num="07" title="Mileage" meta="eResponse.19–22">
        <div className="fgrid">
          <div className="fcol-6"><TextField label="Begin Odometer" elem="eResponse.19" nem={null} /></div>
          <div className="fcol-6"><TextField label="On Scene Odometer" elem="eResponse.20" nem={null} /></div>
          <div className="fcol-6"><TextField label="At Destination Odometer" elem="eResponse.21" nem={null} /></div>
          <div className="fcol-6"><TextField label="End Odometer" elem="eResponse.22" nem={null} /></div>
          <div className="fcol-12">
            <MileageSummary />
          </div>
        </div>
      </Section>

      <Section id="additional" num="08" title="Additional Factors" meta="eScene.* · custom">
        <div className="fgrid">
          <div className="fcol-12">
            <TextField label="Other EMS or Public Safety Agencies at Scene" elem="eScene.02" />
          </div>
          <div className="fcol-12">
            <OptionField label="First Agency to Provide Patient Care" elem="eScene.24"
              inline={false} />
          </div>
          <div className="fcol-12">
            <OptionField label="Additional Responders" elem="eScene.04" />
          </div>
          <div className="fcol-12">
            <TextField label="Additional Comments" path="custom/AdditionalComments" multiline rows={3} />
          </div>
        </div>
      </Section>

    </ViewLayout>
  );
}

function CrewList({ data, crewList }) {
  const [rosterOpen, setRosterOpen] = React.useState(false);
  const [roster, setRoster] = React.useState([]);

  // The roster is a single small static file (~10 rows) — fetched once on
  // mount rather than lazily on open, so the "Add crew member" picker never
  // has to show a loading state.
  React.useEffect(() => {
    let cancelled = false;
    fetch(CREW_ROSTER_URL).then(r => r.json()).then(list => { if (!cancelled) setRoster(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setCrew = (next) => data.setReportPath("eCrew/eCrew.CrewGroup", next);

  const addMember = (id) => {
    const entry = roster.find(r => r.id === id);
    if (!entry) return;
    setCrew([...crewList, { "eCrew.01": entry.id, "eCrew.02": entry.level }]);
    setRosterOpen(false);
  };

  const removeMember = (i) => setCrew(crewList.filter((_, idx) => idx !== i));

  const setRole = (i, key) => data.setReportPath(`eCrew/eCrew.CrewGroup/${i}/eCrew.03`, ROLE_CODES[key]);

  const existingIds = new Set(crewList.map(c => c["eCrew.01"]).filter(Boolean));
  const rosterOptions = roster
    .filter(r => !existingIds.has(r.id))
    .map(r => ({
      value: r.id,
      label: `${r.name} — ${data.label("eCrew.02", r.level) || r.level}`,
      category: r.id.startsWith("P") ? "Paramedic (P-series)" : "EMT-tier (E-series)",
    }));

  return (
    <div className="epcr-crew-list">
      {crewList.map((c, i) => {
        const id = c["eCrew.01"] || "";
        const initials = id.slice(0, 2).toUpperCase();
        const roleKey = roleKeyFromCodes(c["eCrew.03"]);
        return (
          <div key={i} className="epcr-crew-row">
            <div className="epcr-crew-avatar">{initials || "—"}</div>
            <StaticField label="Crew Member ID" value={id} mono />
            <div className="field epcr-crew-role">
              <label className="field-label"><span>Role</span></label>
              <div className="opt-group">
                {ROLE_ORDER.map(([key, label]) => (
                  <button
                    key={key}
                    className={"opt-btn" + (roleKey === key ? " selected blue" : "")}
                    onClick={() => setRole(i, key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button className="icon-btn" onClick={() => removeMember(i)} aria-label="Remove crew member">
              <Icons.trash size={16} />
            </button>
          </div>
        );
      })}
      {crewList.length === 0 && (
        <div className="epcr-crew-empty">No crew members recorded</div>
      )}
      <button className="btn-secondary epcr-crew-add" onClick={() => setRosterOpen(true)}>
        <Icons.plus size={14} /> Add crew member
      </button>
      {rosterOpen && (
        <PickerModal
          title="Add Crew Member"
          options={rosterOptions}
          onClose={() => setRosterOpen(false)}
          onSelect={addMember}
        />
      )}
    </div>
  );
}

function MileageSummary() {
  const begin   = parseFloat(useField("eResponse/eResponse.19"));
  const onScene = parseFloat(useField("eResponse/eResponse.20"));
  const atDest  = parseFloat(useField("eResponse/eResponse.21"));
  const end     = parseFloat(useField("eResponse/eResponse.22"));
  const total    = isFinite(end) && isFinite(begin) ? (end - begin).toFixed(1) : null;
  const loaded   = isFinite(atDest) && isFinite(onScene) ? (atDest - onScene).toFixed(1) : null;
  const deadhead = total != null && loaded != null ? (parseFloat(total) - parseFloat(loaded)).toFixed(1) : null;
  const stats = [["Loaded Miles", loaded], ["Total Miles", total], ["Deadhead Miles", deadhead]];
  return (
    <div className="mileage-summary">
      {stats.map(([label, v]) => (
        <div className="mileage-stat" key={label}>
          <span className="label">{label}</span>
          <span className="value">{v ?? "—"}{v != null && <span className="unit">mi</span>}</span>
        </div>
      ))}
    </div>
  );
}
